import React, { isValidElement, PureComponent } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated, Easing } from 'react-native';
import Theme, { Icon } from '@qnpm/q-theme';
import { computeTitle, computeTitleMargin, computeWrapperBgColor, getNavBarStyles } from './handle';
import { ACTIVE_OPACITY, MAIN_TITLE_SIZE, SUB_TITLE_SIZE } from './constant';
import { NOTCH_HEIGHT } from './styles';
import { qAccessibilityLabel } from '@qnpm/q-design-utils';
// 按钮组位置
var ButtonPosition;
(function (ButtonPosition) {
    ButtonPosition["LEFT"] = "left";
    ButtonPosition["RIGHT"] = "right";
})(ButtonPosition || (ButtonPosition = {}));
const { layout } = Theme;
export default class BaseNavBar extends PureComponent {
    constructor(props) {
        var _a, _b;
        super(props);
        /**
         * 切换标题
         */
        this.onToggleTitle = () => {
            const { title } = this.props;
            const { expand } = this.state;
            const { onToggle } = title;
            const nextState = !expand;
            // 改变状态
            this.setState({ expand: nextState });
            // 回调
            onToggle && onToggle(nextState);
        };
        const initBgColor = this.getBackgroundColor() ||
            computeWrapperBgColor(getNavBarStyles(props));
        // 初始状态
        const initStatus = isValidElement(props.title)
            ? false
            : (_b = (_a = props === null || props === void 0 ? void 0 : props.title) === null || _a === void 0 ? void 0 : _a.expand) !== null && _b !== void 0 ? _b : false;
        this.animation = new Animated.Value(0);
        this.currentColor = initBgColor;
        const { needScale = false } = props;
        const { navbar } = layout;
        this.layoutStyle = needScale ? navbar.scaled : navbar.unscaled;
        this.state = {
            expand: initStatus,
            buttonWidth: {
                left: 0,
                right: 0
            }
        };
    }
    static getDerivedStateFromProps(nextProps, prevState) {
        const { title } = nextProps || {};
        // 如果标题是元素，则直接终止
        if (isValidElement(title)) {
            return null;
        }
        const { expand } = title || {};
        const { expand: prevStatus } = prevState || {};
        // 外部可控制展开收起状态
        if (expand !== undefined && expand !== prevStatus) {
            return { expand };
        }
        return null;
    }
    componentDidUpdate() {
        const backgroundColor = this.getBackgroundColor();
        // 透明度发生变化时，开始动画
        if (backgroundColor !== undefined &&
            backgroundColor !== this.currentColor) {
            this.startAnimation();
        }
    }
    render() {
        // 计算样式
        const computedStyles = getNavBarStyles(this.props);
        const { wrapper, leftButton, rightButton } = computedStyles;
        const backgroundColor = this.getBackgroundColor();
        return (<Animated.View style={[
            styles.wrapper,
            wrapper,
            {
                backgroundColor: this.animation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [this.currentColor, backgroundColor]
                })
            }
        ]}>
                {this.renderButtonView(this.props, ButtonPosition.LEFT, leftButton)}
                {this.renderTitleView(this.props, computedStyles)}
                {this.renderButtonView(this.props, ButtonPosition.RIGHT, rightButton)}
            </Animated.View>);
    }
    /**
     * 渲染按钮区域
     * @param props
     * @param position
     * @param computeStyle
     */
    renderButtonView(props, position, computeStyle) {
        const { leftButton = [], rightButton = [], accessibilityPreName = '' } = props;
        // 是否是左侧按钮
        const isLeft = position === ButtonPosition.LEFT;
        // 根据左右位置设置渲染配置
        const buttonConfigs = isLeft ? leftButton : rightButton;
        // 如果没有配置，则直接返回
        if (!buttonConfigs ||
            !Array.isArray(buttonConfigs) ||
            buttonConfigs.length === 0) {
            return null;
        }
        // 循环渲染按钮
        const content = buttonConfigs.map((item, index) => this.renderButtonItemView(item, index, computeStyle && computeStyle[index]));
        const wrapperStyle = isLeft
            ? styles.leftButtonWrapper
            : styles.rightButtonWrapper;
        return (<View style={[styles.buttonWrapper, wrapperStyle]} {...qAccessibilityLabel(`${accessibilityPreName}qdNavBar#${isLeft ? 'leftbtn' : 'rightbtn'}`)} onLayout={(e) => this.onButtonLayout(position, e)}>
                {content}
            </View>);
    }
    /**
     * 渲染button子项
     * @param buttonConfig
     * @param index
     * @param computeStyle
     */
    renderButtonItemView(buttonConfig, index, computeStyle) {
        // 如果传入了ReactElement，则直接返回渲染
        if (isValidElement(buttonConfig)) {
            const { adaptNotch } = this.props;
            return (<View key={`nav_bar_button_item_${index}`} style={{ marginTop: adaptNotch ? NOTCH_HEIGHT : 0 }}>
                    {buttonConfig}
                </View>);
        }
        const { text, icon, handler, disabled = false } = buttonConfig;
        const { container, icon: iconStyle, text: textStyle } = computeStyle || {};
        const { accessibilityPreName = '' } = this.props;
        return (<TouchableOpacity accessible={false} {...qAccessibilityLabel(`${accessibilityPreName}qdNavBar#item#${index}`)} disabled={disabled} activeOpacity={ACTIVE_OPACITY} style={[styles.buttonItemContainer, container]} onPress={handler} key={`nav_bar_button_item_${index}`}>
                {this.renderIcon(icon, iconStyle)}
                {text ? (<Text style={[
            styles.buttonItemText,
            styles.textCommon,
            textStyle
        ]}>
                        {text}
                    </Text>) : null}
            </TouchableOpacity>);
    }
    /**
     * 渲染icon
     * @param icon
     * @param computeStyle
     */
    renderIcon(icon, computeStyle) {
        // 没有icon时，直接返回
        if (!icon) {
            return null;
        }
        // 如果是react元素，直接返回
        if (isValidElement(icon)) {
            return icon;
        }
        return (<Icon name={icon} needScale={this.props.needScale} size={computeStyle === null || computeStyle === void 0 ? void 0 : computeStyle.fontSize} color={computeStyle === null || computeStyle === void 0 ? void 0 : computeStyle.color}/>);
    }
    /**
     * 渲染标题
     * @param props
     * @param computedStyles
     */
    renderTitleView(props, computedStyles) {
        const { title, accessibilityPreName = '' } = props;
        // 标题传空时，直接不展示
        if (!title) {
            return <View style={styles.tittleContainer}/>;
        }
        // 标题左右偏移量计算
        const adjustTitleMargin = computeTitleMargin(this.layoutStyle, this.state.buttonWidth, props);
        // 如果是react元素，则直接返回
        if (isValidElement(title)) {
            return (<View style={[styles.tittleContainer, adjustTitleMargin]} {...qAccessibilityLabel(`${accessibilityPreName}qdNavBar#title`)}>
                    {title}
                </View>);
        }
        const { title: textTitle, subtitle = '', numberOfLines = 1, subNumberOfLines = 1, titleLimitLength = MAIN_TITLE_SIZE, subTitleLimitLength = SUB_TITLE_SIZE } = title || {};
        // 只有主标题小于2行时才展示副标题，否则展示不下
        const canShowSubTitle = subtitle && ((textTitle && numberOfLines < 2) || !textTitle);
        // 副标题样式
        const { subtitle: subStyle } = computedStyles.title || {};
        return (<View style={[styles.tittleContainer, adjustTitleMargin]} {...qAccessibilityLabel(`${accessibilityPreName}qdNavBar#title`)}>
                {this.renderMainTitle(title, computedStyles, titleLimitLength, accessibilityPreName)}
                {canShowSubTitle ? (<Text numberOfLines={subNumberOfLines} style={[styles.textCommon, subStyle]}>
                        {computeTitle(subtitle, subTitleLimitLength, subNumberOfLines)}
                    </Text>) : null}
            </View>);
    }
    /**
     * 渲染主标题
     * @param title
     * @param computedStyles
     * @param titleLimitLength
     * @param accessibilityPreName
     */
    renderMainTitle(title, computedStyles, titleLimitLength, accessibilityPreName) {
        const { title: textTitle, touchable, expandTitle = '', numberOfLines = 1 } = title || {};
        const { title: titleStyle, arrow: arrowStyle } = computedStyles;
        const { title: mainTitleStyle } = titleStyle || {};
        const getTitle = (_text) => computeTitle(_text, titleLimitLength, numberOfLines);
        // 主标题不存在时，直接返回空
        if (!textTitle) {
            return null;
        }
        // 有点击操作
        if (touchable) {
            const { expand } = this.state;
            return (<TouchableOpacity activeOpacity={ACTIVE_OPACITY} onPress={this.onToggleTitle} style={styles.mainTitleTextContainer} accessible={false} {...qAccessibilityLabel(`${accessibilityPreName}qdNavBar#title#btn`)}>
                    <Text numberOfLines={numberOfLines} style={[styles.textCommon, mainTitleStyle]}>
                        {getTitle(expand ? expandTitle : textTitle)}
                    </Text>
                    {touchable ? (<Icon name={expand ? 'collapse' : 'unfold'} needScale={this.props.needScale} style={{ marginLeft: this.layoutStyle.arrowMargin }} size={arrowStyle === null || arrowStyle === void 0 ? void 0 : arrowStyle.fontSize} color={arrowStyle === null || arrowStyle === void 0 ? void 0 : arrowStyle.color}/>) : null}
                </TouchableOpacity>);
        }
        // 纯展示
        return (<Text numberOfLines={numberOfLines} style={[styles.textCommon, mainTitleStyle]}>
                {getTitle(textTitle)}
            </Text>);
    }
    /**
     * 获取Props中的背景颜色
     */
    getBackgroundColor() {
        const { customStyle } = this.props;
        const { backgroundColor = '#FFFFFF' } = customStyle || {};
        return backgroundColor;
    }
    /**
     * 开始动画
     */
    startAnimation() {
        const { duration = 200 } = this.props;
        // 重置动画
        this.animation.setValue(0);
        Animated.timing(this.animation, {
            toValue: 1,
            duration,
            easing: Easing.inOut(Easing.exp),
            useNativeDriver: false
        }).start(({ finished }) => {
            // 设置下次动画的开始变换颜色
            if (finished) {
                this.currentColor = this.getBackgroundColor();
            }
        });
    }
    /**
     * 保存左右按钮宽度
     * @param position
     * @param e
     */
    onButtonLayout(position, e) {
        const { width } = e.nativeEvent.layout;
        const { buttonWidth } = this.state;
        this.setState({
            buttonWidth: Object.assign(Object.assign({}, buttonWidth), { [position]: width })
        });
    }
}
const styles = StyleSheet.create({
    wrapper: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        overflow: 'hidden'
    },
    tittleContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center'
    },
    // 安卓垂直居中
    textCommon: {
        textAlignVertical: 'center',
        includeFontPadding: false
    },
    mainTitleTextContainer: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    buttonWrapper: {
        position: 'absolute',
        flexDirection: 'row',
        alignItems: 'center',
        zIndex: 1
    },
    leftButtonWrapper: {
        left: 0
    },
    rightButtonWrapper: {
        right: 0
    },
    buttonItemContainer: {
        justifyContent: 'center',
        alignItems: 'center'
    },
    buttonItemText: {
        color: '#666'
    }
});
